import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

function parseRgb(color) {
  const s = String(color || "").trim().toLowerCase();
  if (s === "white") return [255, 255, 255];
  if (s === "black") return [0, 0, 0];
  let m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(s);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  }
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(s);
  return m ? [+m[1], +m[2], +m[3]] : null;
}

function luminance(rgb) {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
}

function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Active pill colors that stay visible on a light page even when the theme primary is white. */
function resolveActivePillColors(primaryColor, secondaryColor) {
  const white = [255, 255, 255];
  const candidates = [primaryColor, secondaryColor, "#0f172a"];
  let bg = "#0f172a";
  for (const c of candidates) {
    const rgb = parseRgb(c);
    if (rgb && contrast(rgb, white) >= 2.4) {
      bg = String(c).trim();
      break;
    }
  }
  const bgRgb = parseRgb(bg) || [15, 23, 42];
  const fgPref = bg === String(primaryColor || "").trim() ? parseRgb(secondaryColor) : null;
  const fg =
    fgPref && contrast(fgPref, bgRgb) >= 3
      ? String(secondaryColor).trim()
      : luminance(bgRgb) > 0.5
        ? "#0f172a"
        : "#ffffff";
  return { bg, fg };
}

/**
 * Horizontal category pills with overflow scroll + side arrows.
 * Arrows always point physically outward (left ← / right →), independent of RTL.
 */
export default function CatalogCategoryPillsStrip({
  options,
  rtl = true,
  align,
  primaryColor,
  secondaryColor,
  className = "",
  id = "categories-section",
  circles = false,
  renderPill,
}) {
  const activePill = resolveActivePillColors(primaryColor, secondaryColor);
  const trackRef = useRef(null);
  const [canStart, setCanStart] = useState(false);
  const [canEnd, setCanEnd] = useState(false);

  const updateOverflow = useCallback(() => {
    const el = trackRef.current;
    if (!el) {
      setCanStart(false);
      setCanEnd(false);
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 4) {
      setCanStart(false);
      setCanEnd(false);
      return;
    }
    const sl = el.scrollLeft;
    if (sl < 0) {
      // Firefox-style RTL: 0 at inline-start, negative toward end
      setCanStart(sl < -4);
      setCanEnd(sl > -max + 4);
    } else {
      // LTR + Chromium RTL: 0 at start, positive toward end
      setCanStart(sl > 4);
      setCanEnd(sl < max - 4);
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    updateOverflow();
    el.addEventListener("scroll", updateOverflow, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateOverflow) : null;
    ro?.observe(el);
    window.addEventListener("resize", updateOverflow);
    const t = window.setTimeout(() => {
      updateOverflow();
      const active = el.querySelector(".category-pill.active");
      if (active) {
        const trackRect = el.getBoundingClientRect();
        const pillRect = active.getBoundingClientRect();
        if (pillRect.left < trackRect.left) {
          el.scrollLeft -= trackRect.left - pillRect.left + 12;
        } else if (pillRect.right > trackRect.right) {
          el.scrollLeft += pillRect.right - trackRect.right + 12;
        }
        updateOverflow();
      }
    }, 150);
    return () => {
      el.removeEventListener("scroll", updateOverflow);
      ro?.disconnect();
      window.removeEventListener("resize", updateOverflow);
      window.clearTimeout(t);
    };
  }, [updateOverflow, options]);

  /** dir: -1 = toward inline-start, +1 = toward inline-end */
  const scrollByDir = (dir, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.max(200, Math.floor(el.clientWidth * 0.7));
    const sl = el.scrollLeft;
    let delta;
    if (sl < 0 || (rtl && sl === 0 && getComputedStyle(el).direction === "rtl")) {
      if (sl < 0) {
        delta = dir < 0 ? amount : -amount;
      } else {
        delta = dir * amount;
      }
    } else {
      delta = dir * amount;
    }
    const next = sl + delta;
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ left: next, behavior: "smooth" });
    } else {
      el.scrollLeft = next;
    }
    window.setTimeout(updateOverflow, 80);
    try {
      if (e?.currentTarget && typeof e.currentTarget.blur === "function") {
        e.currentTarget.blur();
      }
    } catch {}
  };

  const alignAttr =
    align && ["start", "center", "end"].includes(align) ? align : undefined;
  const showChrome = canStart || canEnd;
  // Physical sides: in RTL, inline-start is on the right.
  const canLeft = rtl ? canEnd : canStart;
  const canRight = rtl ? canStart : canEnd;
  const onLeftClick = (e) => scrollByDir(rtl ? 1 : -1, e);
  const onRightClick = (e) => scrollByDir(rtl ? -1 : 1, e);

  return (
    <div
      className={`categories-flat-carousel categories-flat-carousel--scroll${showChrome ? " categories-flat-carousel--has-overflow" : ""}${canLeft ? " categories-flat-carousel--has-left" : ""}${canRight ? " categories-flat-carousel--has-right" : ""}`}
      style={{ overflowAnchor: "none" }}
    >
      {showChrome ? (
        <button
          type="button"
          className="categories-flat-carousel__arrow categories-flat-carousel__arrow--left"
          onClick={onLeftClick}
          disabled={!canLeft}
          aria-label="Scroll categories left"
        >
          <ChevronLeft size={18} aria-hidden />
        </button>
      ) : null}

      <div
        ref={trackRef}
        id={id}
        className={`categories-flat-container categories-flat-container--hscroll catalog-skip-global-palette${circles ? " categories-flat-container--circles" : ""}${className ? ` ${className}` : ""}`}
        data-category-pills-align={alignAttr || (circles ? "center" : undefined)}
        style={{
          ["--cat-pill-active-bg"]: activePill.bg,
          ["--cat-pill-active-fg"]: activePill.fg,
        }}
      >
        {options.map((option) =>
          renderPill ? (
            <React.Fragment key={option.key}>{renderPill(option)}</React.Fragment>
          ) : (
            <button
              key={option.key}
              type="button"
              className={`category-pill${option.active ? " active" : ""}`}
              onClick={option.onActivate}
            >
              {option.label}
            </button>
          )
        )}
      </div>

      {showChrome ? (
        <button
          type="button"
          className="categories-flat-carousel__arrow categories-flat-carousel__arrow--right"
          onClick={onRightClick}
          disabled={!canRight}
          aria-label="Scroll categories right"
        >
          <ChevronRight size={18} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
